import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { ChatParams, Workspace } from "@/types";

interface WorkspaceRow {
  id: string;
  name: string;
  icon: string;
  color: string | null;
  default_model: string | null;
  ai_params_json: string;
  is_default: number;
  created_at: number;
  updated_at: number;
}

function rowToWorkspace(row: WorkspaceRow): Workspace {
  let aiParams: Partial<ChatParams> = {};
  try {
    aiParams = JSON.parse(row.ai_params_json) as Partial<ChatParams>;
  } catch {
    aiParams = {};
  }
  return {
    id: row.id,
    name: row.name,
    icon: row.icon,
    color: row.color,
    defaultModel: row.default_model,
    aiParams,
    isDefault: row.is_default === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const workspaceRepository = {
  async list(): Promise<Workspace[]> {
    const db = await getDb();
    const rows = await db.select<WorkspaceRow[]>("SELECT * FROM workspaces ORDER BY created_at ASC");
    return rows.map(rowToWorkspace);
  },

  async create(name: string): Promise<Workspace> {
    const db = await getDb();
    const now = Date.now();
    const workspace: Workspace = {
      id: createId("ws"),
      name,
      icon: "Layers",
      color: null,
      defaultModel: null,
      aiParams: {},
      isDefault: false,
      createdAt: now,
      updatedAt: now,
    };
    await db.execute(
      `INSERT INTO workspaces (id, name, icon, color, default_model, ai_params_json, is_default, created_at, updated_at)
       VALUES ($1, $2, $3, $4, $5, $6, 0, $7, $8)`,
      [
        workspace.id,
        workspace.name,
        workspace.icon,
        workspace.color,
        workspace.defaultModel,
        JSON.stringify(workspace.aiParams),
        workspace.createdAt,
        workspace.updatedAt,
      ],
    );
    return workspace;
  },

  async rename(id: string, name: string): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE workspaces SET name = $1, updated_at = $2 WHERE id = $3", [name, Date.now(), id]);
  },

  async setIcon(id: string, icon: string): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE workspaces SET icon = $1, updated_at = $2 WHERE id = $3", [icon, Date.now(), id]);
  },

  async setDefaultModel(id: string, defaultModel: string | null): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE workspaces SET default_model = $1, updated_at = $2 WHERE id = $3", [
      defaultModel,
      Date.now(),
      id,
    ]);
  },

  async setAiParams(id: string, aiParams: Partial<ChatParams>): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE workspaces SET ai_params_json = $1, updated_at = $2 WHERE id = $3", [
      JSON.stringify(aiParams),
      Date.now(),
      id,
    ]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM workspaces WHERE id = $1 AND is_default = 0", [id]);
  },
};
